/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import {
  DebtorCollectionDetailDto,
  deleteRemoveDebtorCollectionItem,
  getShowDebtorCollection,
  postDownloadDebtorCollection,
  putUpdateDebtorCollection,
  putUploadDebtorCollectionImage,
} from '@/api-sdk';
import { globalStorePropertiesStore } from '@/stores/creators/creator.global-store-properties';
import { GlobalComponentPropertiesTypes, GTMEventType } from '@/types';
import { sendGTMEvent } from '@/utils';

export enum PriceMode {
  WITHOUT_MARGIN = 'without-margin',
  WITH_MARGIN = 'with-margin',
  NO_PRICES = 'no-prices',
}

export type FgCollectionDetailsState =
  Partial<GlobalComponentPropertiesTypes> & {
    uuid: string;
    collectionDetails: DebtorCollectionDetailDto | null;
    isEditingName: boolean;
    isLoading: boolean;
    error: string | null;
    currentPage: number;
    priceMode: PriceMode;
    margin: string;
    remarks: { front: string; back: string };
    getCollectionDetails: (showLoader?: boolean) => Promise<void>;
    removeProductFromCollection: (productCode: string) => Promise<void>;
    saveCollectionName: (name: string) => Promise<void>;
    uploadCollectionImage: (file: Blob) => Promise<void>;
    generatePdf: () => Promise<void>;
    setConfig: (config: Partial<FgCollectionDetailsState>) => void;
    setEditingName: (editing: boolean) => void;
    setCurrentPage: (page: number) => void;
    setPriceMode: (mode: PriceMode, margin?: string) => void;
    setRemark: (side: 'front' | 'back', remark: string) => void;
  };

export const fgCollectionDetailsStore = createStore<FgCollectionDetailsState>()(
  devtools(
    (set, get) => ({
      ...globalStorePropertiesStore,
      uuid: '',
      collectionDetails: null,
      isEditingName: false,
      isLoading: false,
      error: null,
      currentPage: 1,
      priceMode: PriceMode.WITHOUT_MARGIN,
      margin: '0',
      remarks: { front: '', back: '' },

      getCollectionDetails: async (showLoader = true) => {
        const { debtor, locale, country, uuid, currentPage, margin } = get();
        if (showLoader) {
          set(
            { isLoading: true, error: null },
            false,
            'SET_STATE: [getCollectionDetails] Start loading',
          );
        }
        try {
          const collectionDetails = await getShowDebtorCollection({
            debtorCode: debtor ?? '',
            uuid: uuid ?? '',
            params: {
              page: currentPage,
              pageSize: 8,
              locale: locale ?? 'en_US',
              country: country ?? 'US',
              margin,
            },
          });
          set(
            { collectionDetails, error: null },
            false,
            'SET_STATE: [getCollectionDetails] Update collection details',
          );
        } catch (error) {
          console.error('Error fetching collection details', error);
          set(
            { error: 'errors.somethingWentWrong' },
            false,
            'SET_STATE: [getCollectionDetails] Error',
          );
        } finally {
          set(
            { isLoading: false },
            false,
            'SET_STATE: [getCollectionDetails] Stop loading',
          );
        }
      },

      removeProductFromCollection: async (productCode: string) => {
        const { debtor, uuid, getCollectionDetails } = get();
        try {
          await deleteRemoveDebtorCollectionItem({
            debtorCode: debtor ?? '',
            collectionId: uuid ?? '',
            productCode,
          });
          await getCollectionDetails(false);
        } catch (error) {
          console.error('Error removing product from collection', error);
          set(
            { error: 'errors.somethingWentWrong' },
            false,
            'SET_STATE: [removeProductFromCollection] Error',
          );
        }
      },

      saveCollectionName: async (name: string) => {
        const { debtor, customerId, collectionDetails } = get();

        const newName = name?.trim() ?? '';
        if (!newName) return;

        if (!debtor || !customerId || !collectionDetails) return;

        try {
          const updatedCollection = await putUpdateDebtorCollection({
            debtorCode: debtor,
            collectionId: collectionDetails.id,
            data: {
              collection_name: newName,
            },
          });
          set({
            collectionDetails: {
              ...collectionDetails,
              name: updatedCollection?.name,
            },
          });
          set(
            { isEditingName: false },
            false,
            'SET_STATE: [saveCollectionName] Stop editing',
          );
        } catch (error) {
          console.error('Error saving collection name', error);
          set(
            { error: 'errors.somethingWentWrong' },
            false,
            'SET_STATE: [saveCollectionName] Error',
          );
        }
      },

      uploadCollectionImage: async (file: Blob) => {
        const { debtor, collectionDetails, getCollectionDetails } = get();

        if (!debtor || !collectionDetails) return;

        try {
          const imageUrl = await putUploadDebtorCollectionImage({
            debtorCode: debtor,
            collectionId: collectionDetails.id,
            data: { file },
          });
          set(
            {
              collectionDetails: {
                ...collectionDetails,
                imageUrl,
              },
            },
            false,
            'SET_STATE: [uploadCollectionImage] Update image URL',
          );
          await getCollectionDetails(false);
        } catch (error) {
          console.error('Error uploading collection image', error);
          set(
            { error: 'errors.somethingWentWrong' },
            false,
            'SET_STATE: [uploadCollectionImage] Error',
          );
        }
      },

      setEditingName: (editing: boolean) =>
        set(
          { isEditingName: editing },
          false,
          'SET_STATE: [setEditingName] Update editing state',
        ),

      setCurrentPage: (page: number) =>
        set(
          { currentPage: page },
          false,
          'SET_STATE: [setCurrentPage] Update current page',
        ),

      generatePdf: async () => {
        const {
          debtor,
          locale,
          uuid,
          priceMode,
          margin,
          remarks,
          collectionDetails,
        } = get();
        if (!debtor || !uuid) return;

        set(
          { error: null },
          false,
          'SET_STATE: [generatePdf] Start generating',
        );

        sendGTMEvent(GTMEventType.GENERATE_COLLECTION_PDF, {
          event: 'generate_collection_pdf',
          collection_id: `${uuid}`,
          collection_name: `${collectionDetails?.name}`,
          price_display: `${
            {
              [PriceMode.WITH_MARGIN]: 'marked_up',
              [PriceMode.NO_PRICES]: 'no_price',
              [PriceMode.WITHOUT_MARGIN]: 'cost_price',
            }[priceMode]
          }`,
          margin_percentage:
            priceMode === PriceMode.WITH_MARGIN ? Number(margin) : 0,
        });

        try {
          const debtorCollectionBlob = await postDownloadDebtorCollection(
            {
              debtorCode: debtor,
              collectionId: uuid,
              data: {
                showPrices: priceMode !== PriceMode.NO_PRICES,
                margin:
                  priceMode === PriceMode.WITH_MARGIN ? Number(margin) : 0,
                remarkFront: remarks.front || '',
                remarkBack: remarks.back || '',
              },
              params: {
                locale: locale ?? 'en_US',
              },
            },
            { responseType: 'blob' },
          );

          const url = URL.createObjectURL(debtorCollectionBlob as Blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${collectionDetails?.name ?? 'collection'}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error generating PDF', error);
          set(
            { error: 'errors.somethingWentWrong' },
            false,
            'SET_STATE: [generatePdf] Error',
          );
        }
      },

      setRemark: (side: 'front' | 'back', remark: string) => {
        const { remarks } = get();
        set(
          { remarks: { ...remarks, [side]: remark } },
          false,
          `SET_STATE: [setRemark] Update ${side} remark`,
        );
      },

      setPriceMode: (mode: PriceMode, marginValue = '0') => {
        set(
          {
            priceMode: mode,
            margin: mode === PriceMode.WITH_MARGIN ? marginValue : '0',
          },
          false,
          'SET_STATE: [setPriceMode] Update price mode',
        );
        get().getCollectionDetails(false);
      },

      setConfig: config =>
        set(config, false, 'SET_STATE: [setConfig] Update config properties'),
    }),
    { name: 'FgCollectionDetailsStore' },
  ),
);
